import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectCurrentStep, setCurrentStep } from 'app/sharedSlice';
import SscSolutionDefinitionStep from 'features/snowflake-solution-central/steps/SscSolutionDefinitionStep';
import PrerequisitesStep from 'features/snowflake-solution-central/steps/PrerequisitesStep';
import DeploymentConfigurationStep from 'features/snowflake-solution-central/steps/DeploymentConfigurationStep';
import SnowflakeAccountsStep from 'features/snowflake-solution-central/steps/SnowflakeAccountsStep';
import NativeModuleConfigurationStep from 'features/snowflake-solution-central/steps/NativeModuleConfigurationStep';
import PythonUdfConfigurationStep from 'features/snowflake-solution-central/steps/PythonUdfConfigurationStep';
import SqlUdfConfigurationStep from 'features/snowflake-solution-central/steps/SqlUdfConfigurationStep';
import StreamlitConfigurationStep from 'features/snowflake-solution-central/steps/StreamlitConfigurationStep';
import SpcsConfigurationStep from 'features/snowflake-solution-central/steps/SpcsCofigurationStep';
import SnowflakeAiConfigurationStep from 'features/snowflake-solution-central/steps/SnowflakeAiConfigurationStep';
import CortexAiConfigurationStep from 'features/snowflake-solution-central/steps/CortexAiConfigurationStep';
import CortexAiSearchConfigurationStep from 'features/snowflake-solution-central/steps/CortexAiSearchConfigurationStep';
import SharedDataDatabaseStep from 'features/snowflake-solution-central/steps/SharedDataDatabaseStep';
import SharedDataSchemaStep from 'features/snowflake-solution-central/steps/SharedDataSchemaStep';
import NotebookConfigurationStep from 'features/snowflake-solution-central/steps/NotebookConfigurationStep';
import NativeAppSummaryStep from 'features/snowflake-solution-central/steps/NativeAppSummaryStep';
import { sendMetrics, MetricType } from 'utils/metrics';
import useSnowlakeSolutionCentralSteps from 'features/snowflake-solution-central/steps/snowflakeSolutionCentralSteps';
import { IStep } from 'utils/types';

export default function NativeAppBuilderView(): JSX.Element {
  const steps: IStep[] = useSnowlakeSolutionCentralSteps();
  const dispatch = useAppDispatch();
  const currentStep = useAppSelector(selectCurrentStep);

  const navigate = useNavigate();

  const setStepAndSendMetric = (step: number): void => {
    sendMetrics(MetricType.create_analytics, step.toString());
    dispatch(setCurrentStep(step));
  };

  const getComponent = (name: string, index: number): React.ReactNode => {
    switch (name) {
      case 'SSC Solution definition':
        return (
          <SscSolutionDefinitionStep onBack={() => navigate('/')} onContinue={() => setStepAndSendMetric(index + 1)} />
        );
      case 'Prerequisites':
        return (
          <PrerequisitesStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Deployment configuration':
        return (
          <DeploymentConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Native module configuration':
        return (
          <NativeModuleConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'SSC Solution summary':
        return (
          <NativeAppSummaryStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Snowflake accounts':
        return (
          <SnowflakeAccountsStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Python UDF configuration':
        return (
          <PythonUdfConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'SQL UDF configuration':
        return (
          <SqlUdfConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Streamlit configuration':
        return (
          <StreamlitConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'SPCS configuration':
        return (
          <SpcsConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Snowflake AI configuration':
        return (
          <SnowflakeAiConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Cortex AI configuration':
        return (
          <CortexAiConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Cortex AI Search configuration':
        return (
          <CortexAiSearchConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Shared Data - Database':
        return (
          <SharedDataDatabaseStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Shared Data - Schema':
        return (
          <SharedDataSchemaStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Notebook configuration':
        return (
          <NotebookConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
    }
  };

  return (
    <>
      {steps.map((step, index) => (
        <Fragment key={`${step.name}-${index}`}>{currentStep === index && getComponent(step.name, index)}</Fragment>
      ))}
    </>
  );
}
